import React, { useState, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import FollowUpService from '../services/followUpService';

const FollowUpNotificationBell = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [followUps, setFollowUps] = useState({
    today: [],
    upcoming: [],
    overdue: []
  });
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadFollowUps();
    // Refresh every 5 minutes
    const interval = setInterval(loadFollowUps, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadFollowUps = async () => {
    setLoading(true);
    try {
      // Load upcoming follow-ups
      const upcomingResult = await FollowUpService.getUpcomingFollowUps();
      
      // Load overdue follow-ups
      const overdueResult = await FollowUpService.getOverdueFollowUps();

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      let todayFollowUps = [];
      let upcomingFollowUps = [];
      let overdueFollowUps = [];

      // Process upcoming follow-ups
      if (upcomingResult.success && upcomingResult.data) {
        upcomingResult.data.forEach(followUp => {
          const followUpDate = new Date(followUp.scheduledDate || followUp.nextFollowUpDate);
          followUpDate.setHours(0, 0, 0, 0);
          
          if (followUpDate.getTime() === today.getTime()) {
            todayFollowUps.push(followUp);
          } else if (followUpDate > today) {
            upcomingFollowUps.push(followUp);
          }
        });
      }

      // Process overdue follow-ups
      if (overdueResult.success && overdueResult.data) {
        overdueFollowUps = overdueResult.data;
      }

      setFollowUps({
        today: todayFollowUps,
        upcoming: upcomingFollowUps,
        overdue: overdueFollowUps
      });

      setTotalCount(todayFollowUps.length + upcomingFollowUps.length + overdueFollowUps.length);
    } catch (error) {
      console.error('Error loading follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const renderFollowUpItem = (followUp, type) => {
    const colorClass = type === 'overdue' ? 'bg-red-50 border-red-200' : 
                       type === 'today' ? 'bg-yellow-50 border-yellow-200' : 
                       'bg-green-50 border-green-200';
    
    const iconColor = type === 'overdue' ? 'text-red-600' : 
                      type === 'today' ? 'text-yellow-600' : 
                      'text-green-600';

    return (
      <div key={followUp.leadId || followUp._id} className={`p-3 rounded-lg border ${colorClass} mb-2`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`text-lg ${iconColor}`}>
                {type === 'overdue' ? '🚨' : type === 'today' ? '📅' : '📆'}
              </span>
              <h4 className="font-bold text-gray-800 text-sm">
                {followUp.leadName || 'Unknown Lead'}
              </h4>
            </div>
            {followUp.leadCompany && (
              <p className="text-xs text-gray-600 ml-7">{followUp.leadCompany}</p>
            )}
            <div className="flex items-center space-x-3 mt-2 ml-7 text-xs text-gray-600">
              <span>📞 {followUp.type || 'Call'}</span>
              <span>📅 {formatDate(followUp.scheduledDate || followUp.nextFollowUpDate)}</span>
              {followUp.priority && (
                <span className={`px-2 py-0.5 rounded-full font-medium ${
                  followUp.priority === 'high' ? 'bg-red-100 text-red-700' :
                  followUp.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {followUp.priority}
                </span>
              )}
            </div>
            {followUp.notes && (
              <p className="text-xs text-gray-600 mt-1 ml-7 italic">"{followUp.notes}"</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Bell Icon Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        {totalCount > 0 ? (
          <BellSolidIcon className="h-6 w-6 text-blue-600 animate-pulse" />
        ) : (
          <BellIcon className="h-6 w-6 text-gray-600" />
        )}
        
        {/* Notification Badge */}
        {totalCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-[9999] max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 sticky top-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">🔔 Follow-up Notifications</h3>
                <button
                  onClick={() => setShowDropdown(false)}
                  className="text-white hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-blue-100 mt-1">
                {totalCount} pending follow-up{totalCount !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading...</p>
                </div>
              ) : totalCount === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">✅</div>
                  <h4 className="text-lg font-bold text-gray-700 mb-2">All Clear!</h4>
                  <p className="text-gray-600">No pending follow-ups</p>
                </div>
              ) : (
                <>
                  {/* Overdue */}
                  {followUps.overdue.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-xl">🚨</span>
                        <h4 className="font-bold text-red-600">
                          Overdue ({followUps.overdue.length})
                        </h4>
                      </div>
                      {followUps.overdue.map(followUp => renderFollowUpItem(followUp, 'overdue'))}
                    </div>
                  )}

                  {/* Today */}
                  {followUps.today.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-xl">📅</span>
                        <h4 className="font-bold text-yellow-600">
                          Today ({followUps.today.length})
                        </h4>
                      </div>
                      {followUps.today.map(followUp => renderFollowUpItem(followUp, 'today'))}
                    </div>
                  )}

                  {/* Upcoming */}
                  {followUps.upcoming.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-xl">📆</span>
                        <h4 className="font-bold text-green-600">
                          Upcoming ({followUps.upcoming.length})
                        </h4>
                      </div>
                      {followUps.upcoming.slice(0, 5).map(followUp => renderFollowUpItem(followUp, 'upcoming'))}
                      {followUps.upcoming.length > 5 && (
                        <p className="text-sm text-gray-600 text-center mt-2">
                          +{followUps.upcoming.length - 5} more upcoming
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-3 border-t sticky bottom-0">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  // Dispatch custom event to navigate to follow-ups
                  window.dispatchEvent(new CustomEvent('navigateToFollowUps'));
                }}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              >
                View All Follow-ups →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FollowUpNotificationBell;
